import React, { createContext, useContext, useEffect, useState } from 'react'

export interface AppSettings {
  general: {
    frontendName: string
    logoUrl: string
    faviconUrl: string
    appUrl: string
    fileBaseUrl: string
    clientBaseUrl: string
    vault: {
      accessKeyId: string
      secretAccessKey: string
      serviceUrl: string
    }
  }
  dataLayer: {
    gtm: {
      containerId: string
      enabled: boolean
    }
    facebook: {
      pixelId: string
      accessToken: string
      enabled: boolean
    }
  }
  contact: {
    supportEmail: string
    supportPhone: string
    address: string
  }
  social: {
    facebook: string
    twitter: string
    instagram: string
    linkedin: string
  }
  seo: {
    defaultTitle: string
    defaultDescription: string
    defaultKeywords: string
  }
  localization: {
    currency: string
    language: string
    timezone: string
  }
}

export const DEFAULT_SETTINGS: AppSettings = {
  general: {
    frontendName: 'My Store',
    logoUrl: '',
    faviconUrl: '',
    appUrl: 'http://localhost:3000',
    fileBaseUrl: 'http://localhost:3000/files',
    clientBaseUrl: 'http://localhost:3000',
    vault: {
      accessKeyId: '',
      secretAccessKey: '',
      serviceUrl: '',
    },
  },
  dataLayer: {
    gtm: {
      containerId: '',
      enabled: false,
    },
    facebook: {
      pixelId: '',
      accessToken: '',
      enabled: false,
    },
  },
  contact: {
    supportEmail: '',
    supportPhone: '',
    address: '',
  },
  social: {
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: '',
  },
  seo: {
    defaultTitle: 'My Store',
    defaultDescription: 'Welcome to our store',
    defaultKeywords: 'ecommerce, shop',
  },
  localization: {
    currency: 'USD',
    language: 'en',
    timezone: 'UTC',
  },
}

interface AppSettingsContextType {
  settings: AppSettings
  updateSettings: (newSettings: Partial<AppSettings>) => void
  updateSection: (section: keyof AppSettings, data: any) => void
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(undefined)

export const AppSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings>(() => {
    const saved = localStorage.getItem('appSettings')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        // Merge parsed settings with defaults to ensure new fields are present
        return { ...DEFAULT_SETTINGS, ...parsed }
      } catch (e) {
        console.error('Failed to parse app settings', e)
        return DEFAULT_SETTINGS
      }
    }
    return DEFAULT_SETTINGS
  })

  useEffect(() => {
    localStorage.setItem('appSettings', JSON.stringify(settings))
  }, [settings])

  const updateSettings = (newSettings: Partial<AppSettings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }))
  }

  const updateSection = (section: keyof AppSettings, data: any) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...data,
      },
    }))
  }

  return (
    <AppSettingsContext.Provider value={{ settings, updateSettings, updateSection }}>
      {children}
    </AppSettingsContext.Provider>
  )
}

export const useAppSettings = () => {
  const context = useContext(AppSettingsContext)
  if (context === undefined) {
    throw new Error('useAppSettings must be used within a AppSettingsProvider')
  }
  return context
}
